<script setup lang="ts">
import { computed, ref, toRef, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ApiRequestError } from "@/services/api/httpClient";
import { useAuth } from "@/composables/useAuth";
import { fetchMyContributions } from "@/services/api/contributionApi";
import {
  fetchBuildingFavorites,
  fetchBuildingViews,
  fetchBuildingVisits
} from "@/services/api/explorationApi";
import type {
  BuildingFavorite,
  BuildingViewRecord,
  BuildingVisit
} from "@/types/exploration";
import type { ContributionSummary } from "@/types/contribution";
import { useDrafts, type DraftData } from "@/composables/useDrafts";
import { useDialogFocus } from "@/composables/useDialogFocus";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const router = useRouter();
const route = useRoute();
const auth = useAuth();
const mode = ref<"login" | "register">("login");
const account = ref("");
const password = ref("");
const displayName = ref("");
const toast = ref<{ text: string; kind: "ok" | "err" } | null>(null);
const editingName = ref(false);
const editName = ref("");
const avatarInput = ref<HTMLInputElement | null>(null);
const editNameInput = ref<HTMLInputElement | null>(null);
const dialogRef = ref<HTMLElement | null>(null);

function requestClose(): void {
  emit("close");
}

useDialogFocus(toRef(props, "open"), dialogRef, requestClose);

const myContributions = ref<ContributionSummary[]>([]);
const draftStore = useDrafts();

// 个人中心手风琴面板：投稿 / 点亮打卡 / 浏览历史 / 收藏记录，同时只展开一个
type ProfilePanel = "contributions" | "visits" | "views" | "favorites";
const activePanel = ref<ProfilePanel | null>(null);
const visits = ref<BuildingVisit[]>([]);
const viewHistory = ref<BuildingViewRecord[]>([]);
const favorites = ref<BuildingFavorite[]>([]);
const panelLoading = ref(false);

const panelTitle = computed(() => {
  switch (activePanel.value) {
    case "contributions":
      return "我的投稿";
    case "visits":
      return "点亮打卡";
    case "views":
      return "浏览历史";
    case "favorites":
      return "收藏记录";
    default:
      return "";
  }
});

const panelCount = computed(() => {
  switch (activePanel.value) {
    case "contributions":
      return myContributions.value.length;
    case "visits":
      return visits.value.length;
    case "views":
      return viewHistory.value.length;
    case "favorites":
      return favorites.value.length;
    default:
      return 0;
  }
});

/** 每次展开都重新拉取，保证浏览/收藏/点亮变动即时同步。 */
async function loadPanelData(panel: ProfilePanel) {
  panelLoading.value = true;
  try {
    if (panel === "views") {
      viewHistory.value = await fetchBuildingViews();
    } else if (panel === "favorites") {
      favorites.value = await fetchBuildingFavorites();
    } else if (panel === "visits") {
      visits.value = await fetchBuildingVisits();
    } else {
      myContributions.value = await fetchMyContributions();
    }
  } catch {
    // 加载失败保留旧数据/空态
  } finally {
    panelLoading.value = false;
  }
}

function togglePanel(panel: ProfilePanel) {
  activePanel.value = activePanel.value === panel ? null : panel;
  if (activePanel.value === panel) {
    void loadPanelData(panel);
  }
}

function openBuilding(buildingId: string) {
  emit("close");
  router.push({
    name: "building-detail",
    params: { buildingId },
    query: { from: route.fullPath }
  });
}

function formatPanelDate(value: string): string {
  return (value || "").slice(0, 10);
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;

const user = computed(() => auth.currentUser.value);
const initial = computed(() => (user.value?.displayName || "城").trim().slice(0, 1).toUpperCase());
const avatarSrc = computed(() => user.value?.avatarUrl || "");
const roleLabel = computed(() => {
  const m: Record<string, string> = { admin: "管理员", reviewer: "审核员", user: "共建用户" };
  return m[user.value?.role || "user"] || "共建用户";
});

watch(() => props.open, (open) => {
  if (open) {
    toast.value = null;
    editingName.value = false;
    // 默认展开“我的投稿”，并即时刷新数据
    activePanel.value = user.value ? "contributions" : null;
    if (user.value) {
      void loadPanelData("contributions");
    }
  } else {
    activePanel.value = null;
  }
});

function showToast(text: string, kind: "ok" | "err") {
  toast.value = { text, kind };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 2200);
}

function switchMode(nextMode: "login" | "register") {
  mode.value = nextMode;
}

async function submit() {
  try {
    if (mode.value === "login") {
      await auth.login({ account: account.value, password: password.value });
    } else {
      await auth.register({ account: account.value, password: password.value, displayName: displayName.value });
    }
    password.value = "";
    emit("close");
  } catch (error) {
    showToast(error instanceof ApiRequestError ? error.message : "登录服务暂不可用", "err");
  }
}

async function logout() {
  try {
    await auth.logout();
    emit("close");
  } catch (error) {
    showToast(error instanceof ApiRequestError ? error.message : "退出失败", "err");
  }
}

function startEditName() {
  editName.value = user.value?.displayName || "";
  editingName.value = true;
  nextTick(() => editNameInput.value?.focus());
}

async function saveName() {
  const name = editName.value.trim();
  if (!name || name.length < 2 || name.length > 24) {
    showToast("昵称长度需为 2-24 字", "err");
    return;
  }
  try {
    await auth.updateDisplayName(name);
    editingName.value = false;
    showToast("昵称已更新", "ok");
  } catch (error) {
    showToast(error instanceof ApiRequestError ? error.message : "更新失败", "err");
  }
}

function cancelEditName() {
  editingName.value = false;
}

function openContribution(id: string) {
  emit("close");
  router.push({ name: "contribution-detail", params: { id } });
}
function goSubmitWithDraft(draft?: DraftData) {
  emit("close");
  router.push({
    name: "crowdsource-submit",
    query: draft
      ? { draft: draft.id, ...(draft.kind === "building-archive" ? { type: "BUILDING_INFO" } : {}) }
      : undefined
  });
}

function triggerAvatar() {
  avatarInput.value?.click();
}

async function onAvatarChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    await auth.updateUserAvatar(file);
    showToast("头像已更新", "ok");
  } catch (error) {
    showToast(error instanceof ApiRequestError ? error.message : "上传失败", "err");
  }
  (event.target as HTMLInputElement).value = "";
}
</script>

<template>
  <Teleport to="body">
    <Transition name="auth-fade">
      <div v-if="open" class="auth-backdrop" @click.self="requestClose">
        <div
          ref="dialogRef"
          class="auth-card"
          :class="{ 'is-profile': user }"
          role="dialog"
          aria-modal="true"
          :aria-label="user ? '用户中心' : mode === 'login' ? '登录' : '注册'"
          tabindex="-1"
        >

          <!-- Toast -->
          <Transition name="toast-slide">
            <div v-if="toast" :class="['auth-toast', toast.kind === 'ok' ? 'toast-ok' : 'toast-err']">
              <span class="toast-dot" />
              {{ toast.text }}
            </div>
          </Transition>

          <button class="auth-card-close" type="button" @click="requestClose" aria-label="关闭登录与用户中心">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>

          <!-- ===== LOGGED IN: Profile ===== -->
          <template v-if="user">
            <div class="profile-wrap">
              <div class="profile-body">
                <!-- Avatar -->
                <button class="profile-avatar" type="button" @click="triggerAvatar" title="更换头像" aria-label="更换头像">
                  <img v-if="avatarSrc" :src="avatarSrc" alt="" />
                  <span v-else aria-hidden="true">{{ initial }}</span>
                  <div class="profile-avatar-cam">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </div>
                  <input ref="avatarInput" type="file" accept="image/jpeg,image/png,image/webp" hidden @change="onAvatarChange" />
                </button>

                <!-- Name -->
                <div class="profile-name">
                  <template v-if="!editingName">
                    <span>{{ user.displayName }}</span>
                    <button class="profile-name-edit" @click="startEditName" title="修改昵称">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  </template>
                  <input
                    v-else
                    ref="editNameInput"
                    v-model="editName"
                    class="profile-name-input"
                    maxlength="24"
                    aria-label="昵称"
                    @keyup.enter="saveName"
                    @keyup.escape="cancelEditName"
                    @blur="saveName"
                  />
                </div>
                <span class="profile-role">{{ roleLabel }}</span>

                <div class="profile-actions">
                  <div class="profile-action-item">
                    <button
                      class="profile-action-btn"
                      :class="{ 'is-active': activePanel === 'contributions' }"
                      title="我的投稿"
                      :aria-pressed="activePanel === 'contributions'"
                      @click="togglePanel('contributions')"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    </button>
                    <span class="profile-action-label" :class="{ 'is-active': activePanel === 'contributions' }">投稿</span>
                  </div>
                  <div class="profile-action-item">
                    <button
                      class="profile-action-btn"
                      :class="{ 'is-active': activePanel === 'visits' }"
                      title="点亮打卡"
                      :aria-pressed="activePanel === 'visits'"
                      @click="togglePanel('visits')"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    </button>
                    <span class="profile-action-label" :class="{ 'is-active': activePanel === 'visits' }">打卡</span>
                  </div>
                  <div class="profile-action-item">
                    <button
                      class="profile-action-btn"
                      :class="{ 'is-active': activePanel === 'views' }"
                      title="浏览历史"
                      :aria-pressed="activePanel === 'views'"
                      @click="togglePanel('views')"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </button>
                    <span class="profile-action-label" :class="{ 'is-active': activePanel === 'views' }">历史</span>
                  </div>
                  <div class="profile-action-item">
                    <button
                      class="profile-action-btn"
                      :class="{ 'is-active': activePanel === 'favorites' }"
                      title="收藏记录"
                      :aria-pressed="activePanel === 'favorites'"
                      @click="togglePanel('favorites')"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                    </button>
                    <span class="profile-action-label" :class="{ 'is-active': activePanel === 'favorites' }">收藏</span>
                  </div>
                </div>
              </div>

              <div class="profile-scroll">
                <!-- 手风琴面板：投稿 / 点亮打卡 / 浏览历史 / 收藏记录 -->
                <Transition name="panel-expand">
                  <div v-if="activePanel" :key="activePanel" class="profile-contributions profile-panel">
                    <div class="profile-section-head">
                      <h3>{{ panelTitle }}</h3>
                      <span>{{ panelCount }} 条</span>
                    </div>
                    <div v-if="panelLoading" class="profile-contrib-empty">加载中…</div>
                    <template v-else>
                      <!-- 我的投稿 -->
                      <template v-if="activePanel === 'contributions'">
                        <div v-if="!myContributions.length" class="profile-contrib-empty">
                          暂无投稿，快去分享你的城市记忆吧
                          <button class="profile-contrib-cta" @click="emit('close'); router.push({ name: 'crowdsource-submit' })">去投稿</button>
                        </div>
                        <div v-else class="profile-contrib-list">
                          <button
                            v-for="item in myContributions"
                            :key="item.id"
                            class="profile-contrib-item"
                            @click="openContribution(item.id)"
                          >
                            <span class="contrib-item-type">{{ item.typeLabel }}</span>
                            <strong class="contrib-item-title">{{ item.title }}</strong>
                            <small class="contrib-item-date">{{ item.submittedAt.slice(0, 10) }}</small>
                          </button>
                        </div>
                      </template>

                      <!-- 点亮打卡 -->
                      <template v-else-if="activePanel === 'visits'">
                        <div v-if="!visits.length" class="profile-contrib-empty">
                          暂无点亮记录，去地图或星云点亮第一处记忆吧
                        </div>
                        <div v-else class="profile-contrib-list">
                          <button
                            v-for="item in visits"
                            :key="item.buildingId"
                            class="profile-contrib-item"
                            @click="openBuilding(item.buildingId)"
                          >
                            <span class="contrib-item-type">{{ item.status === "checked_in" ? "已点亮" : "已到达" }}</span>
                            <strong class="contrib-item-title">{{ item.name || item.buildingId }}</strong>
                            <small class="contrib-item-date">{{ item.district || "武汉" }} · {{ formatPanelDate(item.lastSeenAt) }}</small>
                          </button>
                        </div>
                      </template>

                      <!-- 浏览历史 -->
                      <template v-else-if="activePanel === 'views'">
                        <div v-if="!viewHistory.length" class="profile-contrib-empty">
                          暂无浏览记录，去地图上点亮第一处记忆吧
                        </div>
                        <div v-else class="profile-contrib-list">
                          <button
                            v-for="item in viewHistory"
                            :key="item.buildingId"
                            class="profile-contrib-item"
                            @click="openBuilding(item.buildingId)"
                          >
                            <span class="contrib-item-type">浏览</span>
                            <strong class="contrib-item-title">{{ item.name }}</strong>
                            <small class="contrib-item-date">{{ item.district || "武汉" }} · {{ formatPanelDate(item.viewedAt) }}</small>
                          </button>
                        </div>
                      </template>

                      <!-- 收藏记录 -->
                      <template v-else>
                        <div v-if="!favorites.length" class="profile-contrib-empty">
                          暂无收藏，在建筑卡片上点亮星标即可收藏
                        </div>
                        <div v-else class="profile-contrib-list">
                          <button
                            v-for="item in favorites"
                            :key="item.buildingId"
                            class="profile-contrib-item"
                            @click="openBuilding(item.buildingId)"
                          >
                            <span class="contrib-item-type">收藏</span>
                            <strong class="contrib-item-title">{{ item.name }}</strong>
                            <small class="contrib-item-date">{{ item.district || "武汉" }} · {{ formatPanelDate(item.favoritedAt) }}</small>
                          </button>
                        </div>
                      </template>
                    </template>
                  </div>
                </Transition>

                <!-- Drafts -->
                <div v-if="draftStore.drafts.value.length" class="profile-contributions">
                  <div class="profile-section-head">
                    <h3>草稿箱</h3>
                    <span>{{ draftStore.drafts.value.length }} 条</span>
                  </div>
                  <div class="profile-contrib-list">
                    <button
                      v-for="draft in draftStore.drafts.value"
                      :key="draft.id"
                      class="profile-contrib-item"
                      @click="goSubmitWithDraft(draft)"
                    >
                      <span class="contrib-item-type">草稿</span>
                      <strong class="contrib-item-title">{{ draft.title || '未命名' }}</strong>
                      <small class="contrib-item-date">{{ draft.savedAt.slice(0, 10) }}</small>
                    </button>
                  </div>
                </div>
              </div>

              <div class="profile-footer">
                <button class="auth-btn auth-btn-ghost" :disabled="auth.loading.value" @click="logout">
                  {{ auth.loading.value ? "正在退出…" : "退出登录" }}
                </button>
              </div>
            </div>
          </template>

          <!-- ===== LOGGED OUT: Login / Register ===== -->
          <template v-else>
            <div class="auth-layout">
              <!-- Left: Form -->
              <div class="auth-form-side">
                <div class="auth-heading">
                  <p class="auth-heading-label">JIANGCHENG ACCOUNT</p>
                  <h2>{{ mode === "login" ? "欢迎回来" : "创建账号" }}</h2>
                  <p class="auth-heading-desc">
                    {{ mode === "login" ? "登录江城记忆账户" : "加入我们，记录武汉历史建筑" }}
                  </p>
                </div>

                <div class="auth-tabs">
                  <button :class="{ active: mode === 'login' }" @click="switchMode('login')">登录</button>
                  <button :class="{ active: mode === 'register' }" @click="switchMode('register')">注册</button>
                </div>

                <form class="auth-fields" @submit.prevent="submit">
                  <div class="auth-fields-inner">
                    <div v-if="mode === 'register'" class="auth-field">
                      <div class="auth-field-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <input v-model="displayName" placeholder="昵称（2-24 字）" aria-label="昵称" maxlength="24" autocomplete="nickname" />
                    </div>
                    <div class="auth-field">
                      <div class="auth-field-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      </div>
                      <input v-model="account" placeholder="邮箱或手机号" aria-label="邮箱或手机号" autocomplete="username" inputmode="email" />
                    </div>
                    <div class="auth-field">
                      <div class="auth-field-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </div>
                      <input v-model="password" type="password" placeholder="密码（至少 8 位）" aria-label="密码" minlength="8" maxlength="72" autocomplete="current-password" />
                    </div>
                  </div>
                  <button class="auth-btn auth-btn-primary" type="submit" :disabled="auth.loading.value">
                    {{ auth.loading.value ? "处理中…" : mode === "login" ? "登  录" : "创建账号" }}
                  </button>
                </form>
              </div>

              <!-- Right: Brand -->
              <div class="auth-brand-side">
                <div class="auth-brand-inner">
                  <div class="auth-brand-mark">
                    <img src="/images/logo.png" alt="江城星忆" width="48" height="48" style="border-radius:12px" />
                  </div>
                  <h3>江城星忆</h3>
                  <p>武汉城市记忆数字平台<br/>知识图谱 · 空间分析 · 公众共建</p>
                  <ul>
                    <li>3D 星云探索城市文化知识图谱</li>
                    <li>记忆网格评分与空间结构诊断</li>
                    <li>文化路线规划与旧影重绘</li>
                    <li>共建建筑档案，点亮城市记忆</li>
                  </ul>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
